import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import { useToast } from './ToastContext';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, name: string, companyName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      if (!email || !password) {
        throw new Error('Preencha todos os campos');
      }

      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });
      
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('Usuário ou senha inválidos');
        }
        throw error;
      }

      if (data?.user) {
        setUser(data.user);
        navigate('/app/dashboard');
        showToast('Login realizado com sucesso!', 'success');
      }

      return { error: null };
    } catch (error) {
      showToast((error as Error).message, 'error');
      return { error: error as Error };
    }
  };

  const signUp = async (email: string, password: string, name: string, companyName: string) => {
    try {
      if (!email || !password || !name || !companyName) {
        throw new Error('Preencha todos os campos');
      }

      // First, check if user exists
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .single();

      if (existingUser) {
        throw new Error('Este e-mail já está cadastrado');
      }

      // Create auth user
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { 
            name,
            company_name: companyName
          }
        }
      });

      if (signUpError) throw signUpError;

      if (data?.user) {
        // Create profile record
        const { error: profileError } = await supabase
          .from('profiles')
          .insert([{
            id: data.user.id,
            name,
            email,
            role: 'admin',
            status: 'active'
          }]);

        if (profileError) throw profileError;

        // Create company settings
        const { error: companyError } = await supabase
          .from('company_settings')
          .insert([{
            user_id: data.user.id,
            name: companyName,
            email
          }]);

        if (companyError) throw companyError;

        showToast('Conta criada com sucesso! Faça login para continuar.', 'success');
        navigate('/login');
      }

      return { error: null };
    } catch (error) {
      showToast((error as Error).message, 'error');
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      sessionStorage.clear();
      navigate('/login');
      showToast('Logout realizado com sucesso!', 'success');
    } catch (error) {
      console.error('Error signing out:', error);
      showToast('Erro ao fazer logout', 'error');
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}